import { useMemo, useState, useCallback } from 'react';
import { TeamChatTypes } from '@frontend/api-team-chat';
import { useDebouncedValue } from '@frontend/design-system';
import { USERS_SEARCH_DELAY, specialCharactersRegex } from '../constants';
import { useTeamChatApi } from '../providers/team-chat.provider/team-chat-api-providers/use-team-chat-api';
import { useTeamChatSelector } from '../providers/team-chat.provider/team-chat.provider';

const EMPTY_ARRAY: TeamChatTypes.User[] = [];

/**
 * Hook for managing user search functionality with debounced API calls and client-side filtering.
 *
 * This hook provides two search strategies:
 * 1. API-based search: When `shouldFetchConversationUsers` is true, searches are debounced
 *    and sent to the backend API for dynamic results.
 * 2. Client-side filtering: When `shouldFetchConversationUsers` is false, performs instant
 *    local filtering on the `allUsers` list by full name.
 *
 * @returns {string} searchTerm - Current search input value
 * @returns {TeamChatTypes.User[]} usersToDisplay - Filtered/searched users to render
 * @returns {boolean} isLoading - True when waiting for debounce or API results
 * @returns {boolean} isError - True when search encounters an error
 * @returns {Function} onChangeSearchTerm - Handler for search input changes
 * @returns {Function} refetchUsers - Manually trigger search with current term
 */
export const useUserSearch = (filterSpecialChar?: boolean, includeSelfUser?: boolean) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm.trim(), USERS_SEARCH_DELAY);
  // When we start a conversation with the draft icon, we can use @ to filter users, so we want to remove the @ from
  // the search term before sending it to the API, but we still want to keep it in the input field,
  // so we have this option to remove it only for the API search
  const sanitizedDebouncedSearchTerm = useMemo(() => {
    if (!filterSpecialChar) return debouncedSearchTerm;
    return debouncedSearchTerm.replaceAll('@', '').replaceAll(specialCharactersRegex, '').trim();
  }, [debouncedSearchTerm, filterSpecialChar]);

  const trimmedSearchTerm = searchTerm.trim();

  const api = useTeamChatSelector((ctx) => ctx.api);
  const shouldFetchConversationUsers = useTeamChatSelector((ctx) => ctx.shouldFetchConversationUsers);
  const addUserPresence = useTeamChatSelector((ctx) => ctx.addUserPresence);
  const allUsers = useTeamChatSelector((ctx) => (includeSelfUser ? ctx.users : ctx.otherUsers));
  const setUsers = useTeamChatSelector((ctx) => ctx.cache.setUsers);

  const filteredAllUsers = useMemo(
    () => (allUsers ? allUsers?.filter((user) => !user.isDeactivated) : EMPTY_ARRAY),
    [allUsers]
  );

  const { useGetUserByName, useGetUserByNameFn } = useTeamChatApi(api);
  const onSearch = useGetUserByNameFn();

  const onSearchUsers = useCallback(
    async (name: string) => {
      return onSearch(name).then((users) => {
        if (users && users.length) {
          setUsers(addUserPresence(users));
        }
        return users;
      });
    },
    [addUserPresence, onSearch, setUsers]
  );

  const {
    data: searchedUsers,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useGetUserByName(sanitizedDebouncedSearchTerm, {
    enabled: shouldFetchConversationUsers && !debouncedSearchTerm.match(specialCharactersRegex),
    onSettled: (users) => {
      if (users && users.length) {
        setUsers(addUserPresence(users));
      }
    },
    ...(includeSelfUser && { select: (data) => data }),
  });

  const onChangeSearchTerm = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const refetchUsers = useCallback(() => {
    return onSearchUsers(trimmedSearchTerm);
  }, [onSearchUsers, trimmedSearchTerm]);

  /**
   * Determines which users should be displayed based on search mode and state.
   *
   * For API-based search mode:
   * - Returns empty array while user is still typing (pre-debounce)
   * - Returns empty array while waiting for API results after debounce
   * - Returns API search results once debounce is complete
   * - Falls back to all users when no search term
   *
   * For client-side filtering mode:
   * - Instantly filters allUsers by first/last name match
   */
  const usersToDisplay = useMemo(() => {
    if (shouldFetchConversationUsers) {
      const isUserStillTyping = trimmedSearchTerm.length > 0 && trimmedSearchTerm !== debouncedSearchTerm;
      const isWaitingForResults = sanitizedDebouncedSearchTerm.length > 0 && isLoadingUsers;

      // Return empty while typing or waiting for results
      if (isUserStillTyping || isWaitingForResults) {
        return EMPTY_ARRAY;
      }

      // Return API search results if debounced term exists, otherwise show all users
      return sanitizedDebouncedSearchTerm.length > 0 ? searchedUsers : filteredAllUsers;
    }

    // Client-side filtering mode
    if (trimmedSearchTerm.length > 0) {
      const normalizedTerm =
        filterSpecialChar && trimmedSearchTerm.startsWith('@')
          ? trimmedSearchTerm.replace('@', '').toLowerCase()
          : trimmedSearchTerm.toLowerCase();
      return filteredAllUsers?.filter((user) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return fullName.includes(normalizedTerm);
      });
    }

    // Default: show all users
    return filteredAllUsers;
  }, [
    shouldFetchConversationUsers,
    trimmedSearchTerm,
    filteredAllUsers,
    debouncedSearchTerm,
    sanitizedDebouncedSearchTerm,
    isLoadingUsers,
    searchedUsers,
    filterSpecialChar,
  ]);

  /**
   * Determines if search is in progress.
   * True when user is still typing or waiting for API results with API-based search enabled.
   */
  const isLoading = useMemo(
    () =>
      shouldFetchConversationUsers &&
      trimmedSearchTerm.length > 0 &&
      (trimmedSearchTerm !== debouncedSearchTerm.trim() || isLoadingUsers),
    [trimmedSearchTerm, debouncedSearchTerm, isLoadingUsers, shouldFetchConversationUsers]
  );

  return {
    onChangeSearchTerm,
    searchTerm,
    usersToDisplay: usersToDisplay ?? EMPTY_ARRAY,
    isLoading,
    isError: !!usersError,
    refetchUsers,
    onSearchUsers,
    allUsers,
    filteredAllUsers,
  };
};
